/**
 * Add declarations to a package by writing SysML - preview first, then apply.
 *
 * A PANEL, not a dialog: it is the SysML tab of the model tree's New dialog
 * (issue #53), beside the wizards, so it brings its content and its actions and
 * leaves the frame to whoever holds it.
 *
 * The counterpart of "Show source": that hands back the .sysml for a subtree,
 * and this takes .sysml back. Somebody who has a declaration in front of them -
 * in an email, in the wiki, in another program - puts it in the model without
 * retyping it through a form one field at a time.
 *
 * Preview then apply, the same discipline ImportCsvDialog follows, and here the
 * preview is worth more: the server parses, grafts, projects and REGENERATES
 * before rolling the whole thing back, so a clean preview has already proved
 * the model survives the paste. Nothing is written on the strength of typing.
 *
 * Every refusal is shown as a sentence rather than a status. The parser's own
 * message carries the line and column - "expected ';' at 3:14" - which is the
 * one thing that tells somebody where to look, so it is shown verbatim in a
 * monospaced block rather than summarised.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import type { SysmlPasteResult } from '../../schema'

const PLACEHOLDER = `part def Thruster {
    attribute dryMass : ISQ::MassValue;
}`

export default function PasteSysmlPanel({
  pkg,
  target,
  onBusy,
  onClose,
  onPasted,
}: {
  /**
   * The package the declarations go in, already resolved from containment -
   * or '' while the holder is still asking for one.
   */
  pkg: string
  /** The file a new declaration in `pkg` lands in, when it is known. */
  target?: string
  /** Told when a request starts and ends, so the frame can refuse to close mid-write. */
  onBusy?: (busy: boolean) => void
  onClose: () => void
  /** Called after a real apply, so the tree behind can re-read itself. */
  onPasted: (message: string) => void
}) {
  const toasts = useToasts()

  const [text, setText] = useState('')
  const [preview, setPreview] = useState<SysmlPasteResult | null>(null)
  const [busy, setBusyState] = useState(false)
  const [failure, setFailure] = useState('')

  const setBusy = (next: boolean) => {
    setBusyState(next)
    onBusy?.(next)
  }

  // A preview is of THIS package. Choosing another one withdraws it, for the
  // same reason typing does - see change() below.
  useEffect(() => {
    setPreview(null)
    setFailure('')
  }, [pkg])

  /** Turn any refusal into something on the page, never into a thrown render. */
  const explain = (error: unknown) => {
    const failed = error as ApiError
    setPreview(null)
    setFailure(failed?.message ?? 'The paste could not be read.')
  }

  /**
   * Typing invalidates the preview.
   *
   * Without this, editing after a successful preview leaves the Apply button
   * live against text nobody has checked - which is the one way this dialog
   * could write something unproven.
   */
  const change = (value: string) => {
    setText(value)
    setPreview(null)
    setFailure('')
  }

  const check = async () => {
    setBusy(true)
    setFailure('')
    try {
      // dry_run: true is READ-ONLY, and on this route it is a real rehearsal:
      // the server does the whole write and rolls it back.
      setPreview(await api.pasteSysml(pkg, { text, dry_run: true }))
    } catch (error) {
      explain(error)
    } finally {
      setBusy(false)
    }
  }

  const apply = async () => {
    // Only ever from a preview: there is no path to a write that has not been
    // rehearsed against the real model first.
    if (!preview) return
    setBusy(true)
    setFailure('')
    try {
      const result = await api.pasteSysml(pkg, { text, dry_run: false })
      const count = result.added.length
      toasts.succeeded(`${count} declaration${count === 1 ? '' : 's'} added to ${pkg}`)
      onPasted(`Added to ${pkg}`)
    } catch (error) {
      explain(error)
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Write the declarations as they would appear in a <code>.sysml</code> file. Checking
            them runs the whole change against the model and undoes it, so nothing is written
            until you add them.
            {target ? (
              <>
                {' '}They go in <span style={{ fontFamily: MONOSPACE }}>{target}</span>.
              </>
            ) : null}
          </Typography>

          <TextField
            value={text}
            onChange={(event) => change(event.target.value)}
            placeholder={PLACEHOLDER}
            label="SysML"
            multiline
            minRows={8}
            fullWidth
            slotProps={{ input: { sx: { fontFamily: MONOSPACE, fontSize: '0.85rem' } } }}
          />

          {failure && (
            <Alert severity="error">
              <Box sx={{ fontFamily: MONOSPACE, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                {failure}
              </Box>
            </Alert>
          )}

          {preview && (
            <Alert severity="success">
              <Typography variant="body2" gutterBottom>
                This would add {preview.added.length}{' '}
                {preview.added.length === 1 ? 'declaration' : 'declarations'} to{' '}
                {preview.destination}, in {preview.path}. Nothing has been written yet.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mt: 1 }}>
                {preview.added.map((entry, index) => (
                  <Chip
                    key={`${entry.name}:${index}`}
                    size="small"
                    label={`${entry.keyword} ${entry.name}`.trim()}
                    sx={{ fontFamily: MONOSPACE }}
                  />
                ))}
              </Stack>
            </Alert>
          )}

          {busy && <CircularProgress size={18} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button onClick={() => void check()} disabled={busy || !pkg || !text.trim()}>
          Check
        </Button>
        <Button
          variant="contained"
          onClick={() => void apply()}
          // No preview means nothing has been rehearsed, and offering the
          // button invites the click people then wonder about.
          disabled={busy || !preview}
        >
          Add
        </Button>
      </DialogActions>
    </>
  )
}
